import { UserRoles } from '@/interfaces/enums'
import { clearAllStores } from '@/services/clear-stores'
import { useCompanyStore } from '@/store/company'
import { useSessionStore } from '@/store/session'
import { useUserStore } from '@/store/user'
import { storeToRefs } from 'pinia'
import { createRouter, createWebHistory, type NavigationGuardNext, type RouteLocationNormalized, type RouteRecordRaw } from 'vue-router'

import ClassificationAlarmsView from '@/views/Alarms/AlarmsClassificationView.vue'
import AlarmsListingView from '@/views/Alarms/AlarmsListingView.vue'
import AlarmReportView from '@/views/Alarms/AlarmsReportView.vue'
import ClassificationTypeGroupFormView from '@/views/Alarms/ClassificationTypeGroupFormView.vue'
import AnomalyDetectorDetailsView from '@/views/AnomalyDetector/AnomalyDetectorDetailsView.vue'
import AnomalyDetectorView from '@/views/AnomalyDetector/AnomalyDetectorView.vue'
import AccountActivationView from '@/views/Authentication/AccountActivationView.vue'
import ForgotPasswordView from '@/views/Authentication/ForgotPasswordView.vue'
import LoginView from '@/views/Authentication/LoginView.vue'
import RequestActivationTokenView from '@/views/Authentication/RequestActivationTokenView.vue'
import SignupView from '@/views/Authentication/SignupView.vue'
import UpdatePasswordView from '@/views/Authentication/UpdatePasswordView.vue'
import CompanyShiftsView from '@/views/Company/CompanyShiftsView.vue'
import CompanyUsersView from '@/views/Company/CompanyUsersView.vue'
import DashboardCatalogView from '@/views/Dashboard/DashboardCatalogView.vue'
import DashboardView from '@/views/Dashboard/DashboardView.vue'
import DailyTemperatureView from '@/views/Dashboard/DailyTemperatureView.vue'
import CustomDashboardsView from '@/views/Dashboard/CustomDashboardsView.vue'
import FareOptimizerView from '@/views/FareOptimizer/FareOptimizerView.vue'
import GrafanaDashboardsView from '@/views/Grafana/GrafanaDashboardsView.vue'
import CreateGroupView from '@/views/Group/CreateGroupView.vue'
import ListGroupView from '@/views/Group/ListGroupView.vue'
import CreateMaintenanceView from '@/views/Maintenances/CreateMaintenance.vue'
import CreateMaintenanceTypeView from '@/views/Maintenances/CreateMaintenanceTypeView.vue'
import MaintenanceDetailView from '@/views/Maintenances/MaintenanceDetailView.vue'
import MaintenanceListingView from '@/views/Maintenances/MaintenanceListingView.vue'
import MaintenanceReportView from '@/views/Maintenances/MaintenanceReportView.vue'
import UserMaintenanceView from '@/views/Maintenances/UserMaintenanceView.vue'
import ManagementTariffsView from '@/views/Management/ManagementTariffsView.vue'
import ManagementView from '@/views/Management/ManagementView.vue'
import ListNotificationGroupsView from '@/views/NotificationGroups/ListNotificationGroupsView.vue'
import NotificationGroupFormView from '@/views/NotificationGroups/NotificationGroupFormView.vue'
import RateView from '@/views/Rate/RateView.vue'
import RegistersView from '@/views/Registers/RegistersView.vue'
import ReportView from '@/views/Reports/ReportView.vue'
import AlarmDetailView from '@/views/Sensors/AlarmDetailView.vue'
import GroupAlarmDetailView from '@/views/Sensors/GroupAlarmDetailView.vue'
import CreateSensorView from '@/views/Sensors/CreateSensorView.vue'
import ListSensorsView from '@/views/Sensors/ListSensorsView.vue'
import SensorDetailView from '@/views/Sensors/SensorDetailView.vue'
import CompanyUserDetailView from '@/views/UserProfile/CompanyUserDetailView.vue'
import UserProfileView from '@/views/UserProfile/UserProfileView.vue'

import CompanyLayout from '@/layouts/CompanyLayout.vue'

function getCurrentCompanyIdSafe(): string {
  try {
    return String(useCompanyStore().getCurrentCompanyId || 0)
  } catch {
    return '0'
  }
}

function redirectWithCompany(path: string): string {
  return `/company/${getCurrentCompanyIdSafe()}/${path}`
}

function matchedAllowedRoles(to: RouteLocationNormalized): string[] | undefined {
  return to.matched.map((record) => record.meta.allowedRoles).find((roles) => Array.isArray(roles) && roles.length > 0) as string[] | undefined
}

function isUserAllowedForRoute(to: RouteLocationNormalized): boolean {
  const allowed = matchedAllowedRoles(to)
  if (!allowed) return true
  const role = useUserStore().getCurrentUser?.role as string | undefined
  return Boolean(role && allowed.includes(role))
}

function redirectIfRoleDenied(to: RouteLocationNormalized, next: NavigationGuardNext, companyStore: ReturnType<typeof useCompanyStore>): boolean {
  if (isUserAllowedForRoute(to)) return false
  const id = companyStore.getCurrentCompanyId
  if (id !== '' && id !== undefined && id !== null) {
    next({ path: `/company/${id}/dashboard`, replace: true })
    return true
  }
  next({ name: 'login' })
  return true
}

const routes: RouteRecordRaw[] = [
  // Public routes
  { name: 'login', path: '/login', component: LoginView, meta: { public: true } },
  { name: 'forgot-password', path: '/forgot-password', component: ForgotPasswordView, meta: { public: true } },
  { name: 'update-password', path: '/update-password', component: UpdatePasswordView, meta: { public: true, title: 'Redefinir senha' } },
  { name: 'cadastro', path: '/cadastro', component: SignupView, meta: { public: true, title: 'Cadastro' } },
  {
    name: 'account-activation',
    path: '/account-activation',
    component: AccountActivationView,
    meta: { public: true, title: 'Ativar minha conta' },
  },
  {
    name: 'request-activation-token',
    path: '/request-activation-token',
    component: RequestActivationTokenView,
    meta: { public: true, title: 'Solicitar novo código de ativação' },
  },

  // Root redirect — guard will inject correct company
  { name: 'home', path: '/', redirect: () => redirectWithCompany('dashboard') },

  // Company-scoped routes: /company/:companyId/...
  {
    path: '/company/:companyId',
    component: CompanyLayout,
    children: [
      { path: '', redirect: (to) => `/company/${to.params.companyId}/dashboard` },
      { path: 'dashboard', name: 'dashboard', component: DashboardView, meta: { title: 'Dashboard' } },
      { path: 'temperatura-diaria', name: 'temperatura-diaria', component: DailyTemperatureView, meta: { title: 'Temperatura Diária' } },
      { path: 'custom-dashboards', name: 'dashboardCatalog', component: DashboardCatalogView, meta: { title: 'Personalizados' } },
      { path: 'custom-dashboards/:id', name: 'customDashboards', component: CustomDashboardsView, meta: { title: 'Painéis Personalizados' } },

      // Company Users
      {
        path: 'company-users',
        name: 'company-users',
        component: CompanyUsersView,
        meta: {
          title: 'Usuários da Empresa',
          allowedRoles: [UserRoles.ADMIN, UserRoles.MANAGER],
        },
      },

      // Shifts
      {
        path: 'company-shifts',
        name: 'company-shifts',
        component: CompanyShiftsView,
        meta: {
          title: 'Turnos da Empresa',
          allowedRoles: [UserRoles.ADMIN, UserRoles.MANAGER],
        },
      },

      // Sensors
      { path: 'list-sensors', name: 'list-sensors', component: ListSensorsView, meta: { title: 'List Sensors' } },
      { path: 'sensors/:id', name: 'sensor-detail', component: SensorDetailView, meta: { title: 'Detalhes do Sensor' } },
      {
        path: 'sensors/:sensorId/alarm/:alarmId',
        name: 'alarm-detail',
        component: AlarmDetailView,
        meta: { title: 'Detalhe do Alarme' },
      },
      {
        path: 'sensors/group/:groupId/alarm/:alarmId',
        name: 'group-alarm-detail',
        component: GroupAlarmDetailView,
        meta: { title: 'Detalhe do Alarme de Grupo' },
      },
      { path: 'create-sensor', name: 'create-sensor', component: CreateSensorView, meta: { title: 'Create Sensor' } },

      // Groups
      {
        path: 'daily-monitoring',
        name: 'dailyMonitoring',
        component: () => import('@/views/DailyMonitoring/DailyMonitoringView.vue'),
        meta: { title: 'Acompanhamento Diário' },
      },
      { path: 'list-groups', name: 'list-groups', component: ListGroupView, meta: { title: 'List Groups' } },
      { path: 'create-group', name: 'create-group', component: CreateGroupView, meta: { title: 'Create Group' } },

      // Reports
      { path: 'report', name: 'report', component: ReportView },
      { path: 'registers', name: 'registers', component: RegistersView, meta: { title: 'Registers' } },

      // Alarms
      { path: 'alarms/classification', name: 'alarms', component: ClassificationAlarmsView, meta: { title: 'Alarmes' } },
      {
        path: 'alarms/classification/groups/new',
        name: 'classification-type-group-new',
        component: ClassificationTypeGroupFormView,
        meta: { title: 'Novo grupo de classificação' },
      },
      {
        path: 'alarms/classification/groups/:id/edit',
        name: 'classification-type-group-edit',
        component: ClassificationTypeGroupFormView,
        meta: { title: 'Editar grupo de classificação' },
      },
      { path: 'alarms/report', name: 'alarm-report', component: AlarmReportView, meta: { title: 'Relatório de Alarmes' } },
      { path: 'alarms/listing', name: 'alarms-listing', component: AlarmsListingView, meta: { title: 'Listagem de Alarmes' } },

      // Maintenances
      { path: 'maintenances/listing', name: 'maintenance-listing', component: MaintenanceListingView },
      { path: 'maintenances/user-maintenances', name: 'user-maintenances', component: UserMaintenanceView },
      { path: 'maintenances/create', name: 'create-maintenance', component: CreateMaintenanceView, meta: { title: 'Criar Manutenção' } },
      {
        path: 'maintenances/create-types',
        name: 'create-maintenance-types',
        component: CreateMaintenanceTypeView,
        meta: { title: 'Criar Tipos de Manutenção' },
      },
      {
        path: 'maintenances/report',
        name: 'maintenance-report',
        component: MaintenanceReportView,
        meta: {
          title: 'Relatório de Manutenções',
          allowedRoles: [UserRoles.ADMIN, UserRoles.MANAGER],
        },
      },
      {
        path: 'maintenances/:maintenanceId',
        name: 'maintenance-detail',
        component: MaintenanceDetailView,
        meta: { title: 'Detalhe da Manutenção' },
      },

      // User Profile
      { path: 'user-profile', name: 'user-profile', component: UserProfileView },
      {
        path: 'user-profile/company/:profileCompanyId/users/:userId',
        name: 'company-user-detail',
        component: CompanyUserDetailView,
        meta: { title: 'Detalhes do usuário' },
      },
      {
        path: 'user-profile/notification-groups',
        name: 'notification-groups',
        component: ListNotificationGroupsView,
        meta: { title: 'Grupos de notificação' },
      },
      {
        path: 'user-profile/notification-groups/new',
        name: 'notification-groups-new',
        component: NotificationGroupFormView,
        meta: { title: 'Novo grupo de notificação' },
      },
      {
        path: 'user-profile/notification-groups/:id/edit',
        name: 'notification-groups-edit',
        component: NotificationGroupFormView,
        meta: { title: 'Editar grupo de notificação' },
      },

      // Tools
      { path: 'grafana-dashboards', name: 'grafana-dashboards', component: GrafanaDashboardsView, meta: { title: 'Painéis administrativos' } },
      { path: 'contract-optimizer', name: 'contractOptimizer', component: FareOptimizerView, meta: { title: 'Contract Optimizer' } },
      { path: 'anomaly-detector', name: 'anomalyDetector', component: AnomalyDetectorView, meta: { title: 'Detecção de Anomalias' } },
      {
        path: 'anomaly-detector/:sensorId',
        name: 'anomalyDetectorDetails',
        component: AnomalyDetectorDetailsView,
        meta: {
          title: 'Relatório de Anomalias',
        },
      },
      { path: 'rate', name: 'rate', component: RateView },

      // Módulo Gerencial
      {
        path: 'management',
        name: 'management',
        component: ManagementView,
        meta: { title: 'Módulo Gerencial' },
      },
      {
        path: 'management/tariffs',
        name: 'management-tariffs',
        component: ManagementTariffsView,
        meta: { title: 'Tarifas de energia' },
      },
    ],
  },

  // Legacy redirects — old URLs without /company/:id still work
  { path: '/dashboard', redirect: () => redirectWithCompany('dashboard') },
  { path: '/custom-dashboards', redirect: () => redirectWithCompany('custom-dashboards') },
  { path: '/company-users', redirect: () => redirectWithCompany('company-users') },
  { path: '/company-shifts', redirect: () => redirectWithCompany('company-shifts') },
  { path: '/list-sensors', redirect: () => redirectWithCompany('list-sensors') },
  { path: '/sensors/:id(\\d+)', redirect: (to) => redirectWithCompany(`sensors/${to.params.id}`) },
  {
    path: '/sensors/:sensorId(\\d+)/alarm/:alarmId(\\d+)',
    redirect: (to) => redirectWithCompany(`sensors/${to.params.sensorId}/alarm/${to.params.alarmId}`),
  },
  { path: '/list-groups', redirect: () => redirectWithCompany('list-groups') },
  { path: '/create-group', redirect: () => redirectWithCompany('create-group') },
  { path: '/daily-monitoring', redirect: () => redirectWithCompany('daily-monitoring') },
  { path: '/create-sensor', redirect: () => redirectWithCompany('create-sensor') },
  { path: '/report', redirect: () => redirectWithCompany('report') },
  { path: '/registers', redirect: () => redirectWithCompany('registers') },
  { path: '/user-profile', redirect: () => redirectWithCompany('user-profile') },
  {
    path: '/user-profile/company/:profileCompanyId/users/:userId',
    redirect: (to) => redirectWithCompany(`user-profile/company/${to.params.profileCompanyId}/users/${to.params.userId}`),
  },
  { path: '/user-profile/notification-groups', redirect: () => redirectWithCompany('user-profile/notification-groups') },
  { path: '/user-profile/notification-groups/new', redirect: () => redirectWithCompany('user-profile/notification-groups/new') },
  {
    path: '/user-profile/notification-groups/:id/edit',
    redirect: (to) => redirectWithCompany(`user-profile/notification-groups/${to.params.id}/edit`),
  },
  { path: '/alarms/classification', redirect: () => redirectWithCompany('alarms/classification') },
  { path: '/alarms/classification/groups/new', redirect: () => redirectWithCompany('alarms/classification/groups/new') },
  {
    path: '/alarms/classification/groups/:id/edit',
    redirect: (to) => redirectWithCompany(`alarms/classification/groups/${to.params.id}/edit`),
  },
  { path: '/alarms/report', redirect: () => redirectWithCompany('alarms/report') },
  { path: '/alarms/listing', redirect: () => redirectWithCompany('alarms/listing') },
  { path: '/maintenances/listing', redirect: () => redirectWithCompany('maintenances/listing') },
  { path: '/maintenances/user-maintenances', redirect: () => redirectWithCompany('maintenances/user-maintenances') },
  { path: '/maintenances/create', redirect: () => redirectWithCompany('maintenances/create') },
  { path: '/maintenances/create-types', redirect: () => redirectWithCompany('maintenances/create-types') },
  { path: '/maintenances/report', redirect: () => redirectWithCompany('maintenances/report') },
  {
    path: '/maintenances/:maintenanceId',
    redirect: (to) => redirectWithCompany(`maintenances/${to.params.maintenanceId}`),
  },
  { path: '/contract-optimizer', redirect: () => redirectWithCompany('contract-optimizer') },
  { path: '/anomaly-detector', redirect: () => redirectWithCompany('anomaly-detector') },
  { path: '/rate', redirect: () => redirectWithCompany('rate') },
  { path: '/management', redirect: () => redirectWithCompany('management') },
  { path: '/management/tariffs', redirect: () => redirectWithCompany('management/tariffs') },
  { path: '/grafana-dashboards', redirect: () => redirectWithCompany('grafana-dashboards') },
  { path: '/temperatura-diaria', redirect: () => redirectWithCompany('temperatura-diaria') },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { left: 0, top: 0 }
  },
})

router.beforeEach((to, from, next) => {
  const { hasSession } = storeToRefs(useSessionStore())
  const isAuthenticated = hasSession.value

  // Public routes
  if (to.meta.public) {
    return next()
  }

  // Not authenticated
  if (!isAuthenticated) {
    return next({ name: 'login' })
  }

  const companyStore = useCompanyStore()
  const currentId = companyStore.getCurrentCompanyId
  const companyIdParam = to.params.companyId ? Number(to.params.companyId) : null

  // Route has companyId param
  if (companyIdParam && companyIdParam > 0) {
    if (currentId !== companyIdParam) {
      const targetCompany = companyStore.companies.find((c) => c.id === companyIdParam)
      if (targetCompany) {
        companyStore.setCurrentCompany(companyIdParam)
        useUserStore().changeDefaultCompany({ company_id: companyIdParam })
        clearAllStores()
      }
    }
    if (redirectIfRoleDenied(to, next, companyStore)) return
    return next()
  }

  // Route has invalid/zero companyId or is a company-scoped route without proper id — redirect with the real company id
  if (currentId && to.path.startsWith('/company/')) {
    const fixedPath = to.path.replace(/^\/company\/[^/]*/, `/company/${currentId}`)
    if (fixedPath !== to.path) {
      return next({ path: fixedPath, query: to.query, replace: true })
    }
    if (redirectIfRoleDenied(to, next, companyStore)) return
    return next()
  }

  // Not a company-scoped route and authenticated — redirect to company dashboard
  if (currentId) {
    return next({ path: `/company/${currentId}/dashboard`, replace: true })
  }

  return next()
})

export default router
